import { logGreyErrorFromAccounts, logUsageFromAccounts } from 'owa-account-analytics';
import type { CustomDataMap, TraceErrorObject } from 'owa-analytics-types';
import { getClientVersion } from 'owa-config/lib/bootstrapOptions';
import { fetch } from 'owa-fetch';
import { getGuid } from 'owa-guid';
import type {
    CloudEnvironmentDiscoverer,
    CloudEnvironmentDiscovery,
} from '../types/CloudEnvironmentDiscoverer';
import { StepsCustomData } from 'owa-analytics-record-steps';
import { createExponentialBackoffFunction, createRetriableFunction } from 'owa-retriable-function';
import { pluggableAccountFunctions } from '../api/pluggableAccountFunctions';

const OFFICE_CONFIG_SERVICE_URL = 'https://officeclient.microsoft.com/config16processed';
const FEDERATION_PROVIDER_QUERY_PARAMS = '?services=GetFederationProvider&flights=Client.HrdV21';
const OUTLOOK_WEB_URL_QUERY_PARAM = '?services=OutlookWebUrl';

const commonHeaders = {
    'X-Office-Application': '5',
    'X-Office-Platform': 'Web',
    'Enlightened-Hrd-Client': '1',
    'Content-Type': 'application/json',
};

type HrdResponse = {
    environment: string;
    configProviderName: string | undefined;
    endpoint: EndpointResponse[];
    tenantId: string;
    authority_host: string;
    graph: string;
};

type EndpointResponse = {
    type: string;
    authentication_endpoint: string;
    token_endpoint: string;
};

class InstrumentationContext {
    steps: StepsCustomData;
    startTimeOrigin: number;

    constructor() {
        this.startTimeOrigin = performance.now();
        this.steps = new StepsCustomData();
    }

    addStep(stepName: string, data?: CustomDataMap): void {
        this.steps.addStep(stepName, {
            ...data,
            elapsedMs: performance.now() - this.startTimeOrigin,
        });
    }
}

export class OfficeHrdCloudEnvironmentDiscoverer implements CloudEnvironmentDiscoverer {
    private cachedHrdUrl: string | undefined = undefined;

    async discoverConfigForDomain(domain: string): Promise<CloudEnvironmentDiscovery> {
        const instrumentation = new InstrumentationContext();

        const correlationId = getGuid();
        instrumentation.addStep('Begin', { correlationId, isOnline: self.navigator.onLine });

        try {
            instrumentation.addStep('GetHrdUrl');
            const hrdUrl = await this.getHrdUrl(instrumentation, correlationId);

            const getFederationProviderUrl = addDomainQueryParamToHrdUrl(hrdUrl, domain);

            instrumentation.addStep('FetchFromHrdUrl');
            const urlResponse = await fetchWithRetry(getFederationProviderUrl, {
                method: 'GET',
                headers: new Headers({ ...commonHeaders, 'X-CorrelationId': correlationId }),
            });

            instrumentation.addStep('ReadHrdResponse');
            const response = (await urlResponse.json()) as HrdResponse;

            instrumentation.addStep('BuildCloudEnv', {
                configProviderName: response.configProviderName,
                environment: response.environment,
                tenantId: response.tenantId,
            });
            const discovery = await buildCloudEnvironmentDiscovery(
                instrumentation,
                response,
                correlationId
            );

            instrumentation.addStep('End', {
                outcome: 'Success',
                cloudEnvKey: discovery.cloudEnvironment.environmentKey,
                cacheDurationMinutes: discovery.metadata.cacheDurationMinutes,
                lastDiscoveredTimeIso: discovery.metadata.lastDiscoveredTimeIso,
            });
            return discovery;
        } catch (error) {
            // This event will catch any issue in the function stream
            logGreyErrorFromAccounts(
                'OfficeHrdCloudEnvDiscovery_Error',
                pluggableAccountFunctions?.tryGetGlobalSettingsAccountMailboxInfo?.(),
                error instanceof Error ? error : undefined
            );

            instrumentation.addStep('End', {
                outcome: 'Error',
                error: error instanceof Error ? error.message : String(error),
                isOnline: self.navigator.onLine,
            });
            throw error;
        } finally {
            logUsageFromAccounts(
                'OfficeHrdCloudEnvDiscovery',
                pluggableAccountFunctions?.tryGetGlobalSettingsAccountMailboxInfo?.(),
                instrumentation.steps.getCustomDataMapForSteps()
            );
        }
    }

    async getHrdUrl(
        instrumentation: InstrumentationContext,
        correlationId: string
    ): Promise<string> {
        if (this.cachedHrdUrl) {
            instrumentation.addStep('GetHrdUrl_FromCache');
            return this.cachedHrdUrl;
        }

        instrumentation.addStep('GetHrdUrl_FromService');
        const hrdUrl = await fetchHrdUrl(correlationId);
        this.cachedHrdUrl = hrdUrl;

        return hrdUrl;
    }
}

async function buildCloudEnvironmentDiscovery(
    instrumentation: InstrumentationContext,
    hrdResponse: HrdResponse,
    correlationId: string
): Promise<CloudEnvironmentDiscovery> {
    const {
        endpoint: authenticationEndpoint,
        tenantId,
        environment,
        configProviderName,
    } = hrdResponse;

    // Extract MSA and Org authentication endpoints from the array -- MSA is optional, Org is required
    const msaEndpoint = authenticationEndpoint.find(ep => ep.type === 'MSA');
    const orgEndpoint = authenticationEndpoint.find(ep => ep.type === 'OrgId' || ep.type === 'Org');
    if (!orgEndpoint) {
        throw new Error('Org auth endpoint not found');
    }

    instrumentation.addStep('FetchOutlookWebOrigin');
    const { url: outlookWebOrigin, cacheDurationMinutes } =
        await fetchOutlookWebOriginForFederationProvider(configProviderName, correlationId);

    return {
        cloudEnvironment: {
            environmentKey: buildEnvironmentCacheKey(environment, configProviderName),
            msaAuthenticationEndpoint: msaEndpoint?.authentication_endpoint,
            orgAuthenticationEndpoint: orgEndpoint.authentication_endpoint,
            outlookWebOrigin,
            tenantId,
            usesGlobalDirectory: environment === 'Global',
        },
        metadata: {
            lastDiscoveredTimeIso: new Date().toISOString(),
            cacheDurationMinutes,
        },
    };
}

function buildEnvironmentCacheKey(
    environment: string,
    configProviderName: string | undefined
): string {
    return new URLSearchParams({
        src: 'OfficeHrd',
        env: environment,
        configProv: configProviderName ?? '',
    }).toString();
}

async function fetchHrdUrl(correlationId: string): Promise<string> {
    const getFederationProviderUrl = OFFICE_CONFIG_SERVICE_URL + FEDERATION_PROVIDER_QUERY_PARAMS;

    const { url } = await fetchUrlFromOfficeConfigService(getFederationProviderUrl, correlationId);
    return url;
}

function addDomainQueryParamToHrdUrl(hrdUrl: string, domain: string) {
    return hrdUrl + `?domain=${domain}&v2endpoint=1`;
}

type OfficeConfigServiceResult = {
    url: string;
    cacheDurationMinutes: number | undefined;
};

async function fetchOutlookWebOriginForFederationProvider(
    configProviderName: string | undefined,
    correlationId: string
): Promise<OfficeConfigServiceResult> {
    const officeClientServicesUrl = OFFICE_CONFIG_SERVICE_URL + OUTLOOK_WEB_URL_QUERY_PARAM;
    const getOutlookWebUrl = addFederationProviderQueryParamToUrl(
        officeClientServicesUrl,
        configProviderName
    );

    return fetchUrlFromOfficeConfigService(getOutlookWebUrl, correlationId);
}

function addFederationProviderQueryParamToUrl(url: string, configProviderName: string | undefined) {
    // The order matters, if we do the fp query param first, the config service yields no results
    // If configProviderName is undefined, we don't add the param and it will default to the Global config provider
    return url + (configProviderName ? `&fp=${configProviderName}` : '');
}

async function fetchUrlFromOfficeConfigService(
    requestUrl: string,
    correlationId: string
): Promise<OfficeConfigServiceResult> {
    const urlResponse = await fetchWithRetry(requestUrl, {
        method: 'GET',
        headers: new Headers({
            ...commonHeaders,
            'X-CorrelationId': correlationId,
            'X-Office-Version': getClientVersion(),
            accept: 'application/json',
        }),
    });

    const cacheDurationMinutes = parseCacheDurationHeader(urlResponse);

    const response = await urlResponse.json();
    const requestedUrl = response?.['o:OfficeConfig']?.['o:services']?.['o:service']?.['o:url'];

    if (!requestedUrl) {
        const wrappedError: TraceErrorObject = new Error(
            'GetAccountCloudEnvironment_UrlFetch_EmptyUrl'
        );
        wrappedError.additionalInfo = { requestUrl };
        throw wrappedError;
    }

    return { url: requestedUrl, cacheDurationMinutes };
}

const CACHE_DURATION_HEADER = 'x-office-cacheduration';

function parseCacheDurationHeader(response: Response): number | undefined {
    const headerValue = response.headers.get(CACHE_DURATION_HEADER);
    if (headerValue) {
        const parsed = parseInt(headerValue, 10);
        if (!isNaN(parsed) && parsed >= 0) {
            return parsed;
        }
    }
    return undefined;
}

const createRetriableFunc = createRetriableFunction({
    maximumAttempts: 3,
    timeBetweenRetryInMS: createExponentialBackoffFunction(1000),
});

function fetchWithRetry(url: RequestInfo, init: RequestInit): ReturnType<typeof fetchWithTimeout> {
    return createRetriableFunc(() => fetchWithTimeout(url, init)).retriableFunc();
}

function fetchWithTimeout(url: RequestInfo, init: RequestInit): ReturnType<typeof fetch> {
    return new Promise<Response>((resolve, reject) => {
        let controller: AbortController | undefined;
        if ('AbortController' in self) {
            controller = new AbortController();
            init.signal = controller.signal;
        }

        const timeoutMS = 90000;
        const timerHandle: number = self.setTimeout(() => {
            const timeoutError = new Error('Office HRD request timeout');
            if (controller) {
                controller.abort(timeoutError);
            } else {
                reject(timeoutError);
            }
        }, timeoutMS);

        fetch(url, init)
            .then(response => {
                self.clearTimeout(timerHandle);
                resolve(response);
            })
            .catch(err => {
                self.clearTimeout(timerHandle);
                reject(err);
            });
    });
}
